<template>
  <div class="company-country-selector">
    <Popover v-slot="{ open }" as="div" class="position-relative">
      <Float
        as="div"
        placement="bottom-start"
        :offset="5"
        shift
        adaptive-width
        enter="transition duration-200 ease-out"
        enter-from="opacity-0"
        enter-to="opacity-100"
        leave="transition duration-75 ease-in"
        leave-from="opacity-100"
        leave-to="opacity-0"
        :show="open"
      >
        <div class="position-relative">
          <PopoverButton as="template">
            <button
              type="button"
              class="form-control ccs-trigger"
              :aria-label="$t('common.select_companies')"
            >
              <span class="ccs-trigger__label">
                <span class="ccs-trigger__summary">{{ summaryLabel }}</span>
                <span class="ccs-trigger__count"
                  >{{ model.length }}/{{ companies.length }}</span
                >
              </span>
              <FontAwesomeIcon
                class="ccs-trigger__chevron"
                icon="fas fa-chevron-down"
                :class="{ 'rotate-180': open }"
              />
            </button>
          </PopoverButton>
        </div>

        <PopoverPanel class="ccs-panel">
          <!-- Country quick-filter chips. With a single country (one group)
               there is nothing to filter between, so the row is hidden — the
               toolbar's select/clear-all already covers that case. -->
          <div v-if="groupViews.length > 1" class="ccs-chips">
            <span
              v-for="group in groupViews"
              :key="group.code"
              class="ccs-chip"
              :class="`ccs-chip--${group.state}`"
            >
              <button
                type="button"
                class="ccs-chip__toggle"
                :aria-pressed="group.state === 'on'"
                @click="toggleCountry(group.code)"
              >
                <span
                  v-if="group.state === 'off'"
                  class="ccs-chip__add"
                  aria-hidden="true"
                  >+</span
                >
                <span
                  v-else-if="group.state === 'partial'"
                  class="ccs-chip__dot"
                  aria-hidden="true"
                />
                <span class="ccs-chip__label">{{ group.label }}</span>
                <span class="ccs-chip__count"
                  >{{ group.selected }}/{{ group.companies.length }}</span
                >
              </button>
              <button
                type="button"
                class="ccs-chip__only"
                :title="
                  $t('dashboard.consolidated.selector_show_only', {
                    country: group.label,
                  })
                "
                @click="onlyCountry(group.code)"
              >
                {{ $t("dashboard.consolidated.selector_only") }}
              </button>
              <button
                v-if="group.state !== 'off'"
                type="button"
                class="ccs-chip__remove"
                :aria-label="
                  $t('dashboard.consolidated.selector_remove_country', {
                    country: group.label,
                  })
                "
                @click="setCountry(group.code, false)"
              >
                <FontAwesomeIcon icon="fas fa-xmark" />
              </button>
            </span>
          </div>

          <!-- Per-company search -->
          <div class="ccs-search">
            <FontAwesomeIcon icon="fas fa-magnifying-glass" />
            <input
              v-model="searchQuery"
              type="text"
              :placeholder="$t('common.search')"
              :aria-label="$t('common.search')"
            />
          </div>

          <!-- Global select all / clear all -->
          <div class="ccs-toolbar">
            <span class="ccs-toolbar__meta">
              {{
                $t("dashboard.consolidated.selector_selected_count", {
                  selected: model.length,
                  total: companies.length,
                })
              }}
            </span>
            <div class="ccs-toolbar__actions">
              <button
                type="button"
                class="ccs-link"
                :disabled="model.length === companies.length"
                @click="selectAll"
              >
                {{ $t("common.select_all") }}
              </button>
              <button
                type="button"
                class="ccs-link ccs-link--muted"
                :disabled="model.length === 0"
                @click="clearAll"
              >
                {{ $t("common.clear_all") }}
              </button>
            </div>
          </div>

          <!-- Flat company list, grouped by country -->
          <div class="ccs-list">
            <template v-for="group in filteredGroups" :key="group.code">
              <!-- Only label the country when more than one group is in view;
                   a lone group makes the subhead redundant. -->
              <div v-if="filteredGroups.length > 1" class="ccs-subhead">
                <span>{{ group.label }}</span>
                <span>{{ group.selected }}/{{ group.companies.length }}</span>
              </div>
              <button
                v-for="company in group.companies"
                :key="company.id"
                type="button"
                class="ccs-row"
                :aria-pressed="selectedSet.has(company.id)"
                @click="toggleCompany(company.id)"
              >
                <span
                  class="ccs-check"
                  :class="{ 'ccs-check--on': selectedSet.has(company.id) }"
                >
                  <FontAwesomeIcon
                    v-if="selectedSet.has(company.id)"
                    icon="fas fa-check"
                  />
                </span>
                <span class="ccs-row__name">
                  <span class="ccs-row__title">{{ company.name }}</span>
                </span>
              </button>
            </template>
            <div v-if="filteredGroups.length === 0" class="ccs-empty">
              {{ $t("common.no_results") }}
            </div>
          </div>
        </PopoverPanel>
      </Float>
    </Popover>

    <!-- Polite screen-reader announcement of the current selection. -->
    <span class="visually-hidden" role="status" aria-live="polite">
      {{ summaryLabel }}
    </span>
  </div>
</template>

<script setup lang="ts">
import type { Company } from "@/types";
import {
  groupCompaniesByCountry,
  translateCountryCode,
} from "@/utils/countries";
import { Float } from "@headlessui-float/vue";
import { Popover, PopoverButton, PopoverPanel } from "@headlessui/vue";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";

/** A company guaranteed to carry a numeric id, as fed by the consolidated view. */
type SelectableCompany = Company & { id: number };

const props = defineProps<{
  companies: SelectableCompany[];
}>();

/** Selected company ids; an empty selection shows no data in the dashboard. */
const model = defineModel<number[]>({ required: true });

const { t, te, locale } = useI18n();

const searchQuery = ref("");

const selectedSet = computed(() => new Set(model.value));

const getCountryLabel = (code: string) => translateCountryCode(code, t, te);

const sortedCompanies = computed(() =>
  [...props.companies].sort((a, b) =>
    (a.name || "").localeCompare(b.name || "", locale.value)
  )
);

/** All countries with their companies — drives both the chips and the list. */
const groups = computed(() =>
  groupCompaniesByCountry(sortedCompanies.value, getCountryLabel, locale.value)
);

type GroupState = "on" | "partial" | "off";

/**
 * Each country group enriched with its current selection state, derived once
 * per selection/locale change. The chips, the summary and the handlers all read
 * these cached fields instead of re-scanning a group's companies on every
 * render.
 */
const groupViews = computed(() =>
  groups.value.map((group) => {
    const selected = group.companies.reduce(
      (count, company) => count + (selectedSet.value.has(company.id) ? 1 : 0),
      0
    );
    const state: GroupState =
      selected === 0
        ? "off"
        : selected === group.companies.length
          ? "on"
          : "partial";
    return { ...group, selected, state };
  })
);

/** Country code -> its enriched view, so the chip handlers reuse the bucketing. */
const groupByCode = computed(
  () => new Map(groupViews.value.map((group) => [group.code, group]))
);

const normalizedQuery = computed(() => searchQuery.value.trim().toLowerCase());

/**
 * Groups narrowed to companies matching the search, dropping emptied groups.
 * Each kept group carries its own `selected` count over the *filtered*
 * companies so the subhead reads it directly.
 */
const filteredGroups = computed(() => {
  const query = normalizedQuery.value;
  if (!query) {
    return groupViews.value;
  }
  return groupViews.value
    .map((group) => {
      const companies = group.companies.filter((company) =>
        (company.name || "").toLowerCase().includes(query)
      );
      return {
        ...group,
        companies,
        selected: companies.reduce(
          (count, company) =>
            count + (selectedSet.value.has(company.id) ? 1 : 0),
          0
        ),
      };
    })
    .filter((group) => group.companies.length > 0);
});

const summaryLabel = computed(() => {
  const total = props.companies.length;
  const selected = model.value.length;

  if (total === 0 || selected === 0) {
    return t("dashboard.consolidated.selector_no_companies");
  }
  if (selected === total) {
    return t("dashboard.consolidated.selector_all_companies");
  }

  const touched = groupViews.value.filter((group) => group.selected > 0);
  const fullLabels = touched
    .filter((group) => group.selected === group.companies.length)
    .map((group) => group.label);
  const allWhole = fullLabels.length === touched.length;

  // Whole-country selections with nothing partial → just name the countries
  // (one or two of them; three or more falls through to the count summary).
  if (allWhole && fullLabels.length <= 2) {
    return fullLabels.join(" + ");
  }
  if (touched.length === 1) {
    return t("dashboard.consolidated.selector_company_count", {
      count: selected,
    });
  }
  return t("dashboard.consolidated.selector_countries_companies", {
    countries: touched.length,
    companies: selected,
  });
});

const toggleCompany = (id: number) => {
  const next = new Set(model.value);
  if (next.has(id)) {
    next.delete(id);
  } else {
    next.add(id);
  }
  model.value = [...next];
};

const setCountry = (code: string, on: boolean) => {
  const group = groupByCode.value.get(code);
  if (!group) {
    return;
  }
  const next = new Set(model.value);
  for (const company of group.companies) {
    if (on) {
      next.add(company.id);
    } else {
      next.delete(company.id);
    }
  }
  model.value = [...next];
};

const toggleCountry = (code: string) => {
  const group = groupByCode.value.get(code);
  if (group) {
    setCountry(code, group.state !== "on");
  }
};

const onlyCountry = (code: string) => {
  model.value =
    groupByCode.value.get(code)?.companies.map((company) => company.id) ?? [];
};

const selectAll = () => {
  model.value = props.companies.map((company) => company.id);
};

const clearAll = () => {
  model.value = [];
};
</script>

<style scoped lang="scss">
.company-country-selector {
  width: 100%;
}

/* ---- Trigger ---- */
.ccs-trigger {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  min-height: 44px;
  width: 100%;
  padding: 0 1rem;
  text-align: left;
  cursor: pointer;
}

.ccs-trigger__label {
  display: inline-flex;
  align-items: baseline;
  min-width: 0;
  overflow: hidden;
}

.ccs-trigger__summary {
  font-weight: 600;
  color: var(--phoenix-body-color);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.ccs-trigger__count {
  flex-shrink: 0;
  color: var(--phoenix-secondary-color);
  font-variant-numeric: tabular-nums;
  white-space: nowrap;

  &::before {
    content: "·";
    margin: 0 7px;
    color: var(--phoenix-quaternary-color);
  }
}

.ccs-trigger__chevron {
  flex-shrink: 0;
  color: var(--phoenix-secondary-color);
  transition: transform 0.2s ease;

  &.rotate-180 {
    transform: rotate(180deg);
  }
}

/* ---- Popover panel ---- */
.ccs-panel {
  /* Matches the trigger width via Float's adaptive-width, so the panel sits
     exactly below the (now full-width) trigger. */
  width: 100%;
  background: var(--phoenix-emphasis-bg);
  border: 1px solid var(--phoenix-border-color);
  border-radius: var(--phoenix-border-radius-lg);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.08);
  overflow: hidden;
  z-index: 1050;
}

/* ---- Country quick-filter chips ---- */
.ccs-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  padding: 12px 14px;
  border-bottom: 1px solid var(--phoenix-gray-200);
}

.ccs-chip {
  display: inline-flex;
  align-items: center;
  border-radius: var(--phoenix-border-radius-pill);
  border: 1px solid var(--phoenix-border-color);
  background: var(--phoenix-emphasis-bg);
  color: var(--phoenix-secondary-color);
  font-size: 12px;
  font-weight: 600;
  overflow: hidden;
  transition:
    border-color 0.12s ease,
    background-color 0.12s ease,
    color 0.12s ease;

  &:hover {
    border-color: var(--phoenix-gray-500);
    color: var(--phoenix-body-color);
  }
}

.ccs-chip__toggle {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 5px 8px 5px 11px;
  background: transparent;
  border: 0;
  font: inherit;
  color: inherit;
  cursor: pointer;

  &:focus-visible {
    outline: 2px solid var(--phoenix-primary);
    outline-offset: -2px;
  }
}

.ccs-chip__add {
  font-size: 14px;
  line-height: 1;
  color: var(--phoenix-quaternary-color);
}

.ccs-chip:hover .ccs-chip__add {
  color: var(--phoenix-primary);
}

.ccs-chip__dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: var(--phoenix-warning);
  flex-shrink: 0;
}

.ccs-chip__count {
  font-variant-numeric: tabular-nums;
  font-size: 11px;
  font-weight: 700;
  opacity: 0.7;
}

/* "only" action — isolate a single country */
.ccs-chip__only {
  background: transparent;
  border: 0;
  font: inherit;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--phoenix-quaternary-color);
  padding: 2px 6px;
  border-radius: var(--phoenix-border-radius-pill);
  cursor: pointer;
  transition:
    color 0.12s ease,
    background-color 0.12s ease;

  &:hover {
    color: var(--phoenix-primary);
    background: rgba(var(--phoenix-primary-rgb), 0.12);
  }

  &:focus-visible {
    outline: 2px solid var(--phoenix-primary);
    outline-offset: 1px;
  }

  /* Unselected chips end on "ONLY" — give it breathing room from the edge. */
  &:last-child {
    margin-right: 8px;
  }
}

.ccs-chip:hover .ccs-chip__only {
  color: var(--phoenix-secondary-color);
}

/* remove button */
.ccs-chip__remove {
  all: unset;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  margin-right: 6px;
  border-radius: var(--phoenix-border-radius-pill);
  color: var(--phoenix-primary-text-emphasis);
  opacity: 0.7;
  font-size: 9px;
  cursor: pointer;

  &:hover {
    background: rgba(var(--phoenix-primary-rgb), 0.15);
    opacity: 1;
  }

  &:focus-visible {
    outline: 2px solid var(--phoenix-primary);
    outline-offset: 1px;
    background: rgba(var(--phoenix-primary-rgb), 0.15);
    opacity: 1;
  }
}

/* selected (on + partial) */
.ccs-chip--on,
.ccs-chip--partial {
  background: var(--phoenix-primary-bg-subtle);
  border-color: rgba(var(--phoenix-primary-rgb), 0.18);
  color: var(--phoenix-primary-text-emphasis);

  &:hover {
    border-color: rgba(var(--phoenix-primary-rgb), 0.3);
    color: var(--phoenix-primary-text-emphasis);
  }
}

.ccs-chip--partial {
  border-style: dashed;
}

/* ---- Search ---- */
.ccs-search {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  border-bottom: 1px solid var(--phoenix-gray-200);
  color: var(--phoenix-quaternary-color);

  input {
    flex: 1;
    border: 0;
    outline: 0;
    background: transparent;
    font: inherit;
    color: var(--phoenix-body-color);

    &::placeholder {
      color: var(--phoenix-quaternary-color);
    }
  }
}

/* ---- Toolbar ---- */
.ccs-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 8px 12px;
  border-bottom: 1px solid var(--phoenix-gray-200);
}

.ccs-toolbar__meta {
  font-size: 12px;
  color: var(--phoenix-secondary-color);
  font-variant-numeric: tabular-nums;
}

.ccs-toolbar__actions {
  display: flex;
  gap: 4px;
}

.ccs-link {
  border: 0;
  background: transparent;
  font: inherit;
  font-size: 13px;
  font-weight: 600;
  color: var(--phoenix-primary);
  padding: 4px 6px;
  border-radius: 6px;
  cursor: pointer;

  &:hover {
    background: var(--phoenix-primary-bg-subtle);
  }

  &:disabled {
    color: var(--phoenix-quaternary-color);
    background: transparent;
    cursor: default;
  }

  &--muted {
    color: var(--phoenix-secondary-color);

    &:hover {
      background: var(--phoenix-body-highlight-bg);
    }
  }
}

/* ---- List ---- */
.ccs-list {
  max-height: 300px;
  overflow-y: auto;
  overscroll-behavior: contain;
}

.ccs-subhead {
  position: sticky;
  top: 0;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 5px 14px;
  background: var(--phoenix-body-highlight-bg);
  font-size: 10.5px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--phoenix-secondary-color);
  font-variant-numeric: tabular-nums;
}

.ccs-row {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 8px 14px;
  background: transparent;
  border: 0;
  text-align: left;
  font: inherit;
  color: inherit;
  cursor: pointer;

  /* Lighter than the gray-100 subhead so the two don't blend together. */
  &:hover {
    background: var(--phoenix-gray-50);
  }

  &:focus-visible {
    outline: 2px solid var(--phoenix-primary);
    outline-offset: -2px;
  }
}

.ccs-row__name {
  flex: 1;
  min-width: 0;
}

.ccs-row__title {
  display: block;
  font-weight: 500;
  color: var(--phoenix-body-color);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.ccs-check {
  width: 18px;
  height: 18px;
  flex-shrink: 0;
  border-radius: 5px;
  border: 1.5px solid var(--phoenix-gray-400);
  background: var(--phoenix-emphasis-bg);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 11px;

  &--on {
    background: var(--phoenix-primary);
    border-color: var(--phoenix-primary);
  }
}

.ccs-empty {
  padding: 14px;
  color: var(--phoenix-secondary-color);
}
</style>
